import React, { useContext, useMemo, useState } from "react";
import { useQuery, gql } from "@apollo/client";
import {
  Box, Container, Typography, Paper, Chip, Stack,
  CircularProgress, Button,
} from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelIcon from "@mui/icons-material/Cancel";
import InboxIcon from "@mui/icons-material/Inbox";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import EventIcon from "@mui/icons-material/Event";
import { useNavigate } from "react-router-dom";
import { Header } from "../../components/Header";
import { myTickets } from "../../apollo/server";
import UserContext from "../../context/User";

const MY_TICKETS = gql`${myTickets}`;

const ISSUE_LABELS = {
  MISSING_ITEM: "Missing Item", INCORRECT_ITEM: "Incorrect Item",
  WRONG_ORDER: "Wrong Order", FOOD_QUALITY: "Food Quality",
  NEVER_ARRIVED: "Never Arrived", WRONG_ADDRESS_DROP: "Wrong Drop Address",
  DRIVER_ISSUE: "Driver Issue", ORDER_LATE: "Order Late",
  TECH_BILLING: "Tech/Billing", OTHER: "Other",
};

// Bucket a raw status into one of three categories used by the filter pills.
function bucketOf(status) {
  if (status === "RESOLVED") return "RESOLVED";
  if (status === "CLOSED_NO_ACTION") return "CLOSED";
  return "OPEN"; // any pending / under review status
}

const FILTERS = [
  { key: "ALL", label: "All tickets", icon: InboxIcon },
  { key: "OPEN", label: "Under review", icon: HourglassEmptyIcon },
  { key: "RESOLVED", label: "Resolved", icon: CheckCircleIcon },
  { key: "CLOSED", label: "Closed", icon: CancelIcon },
];

// What a resolved refund became, for the pill.
function resolvedLabel(refund) {
  if (!refund || refund.approvedAmount == null) return "Resolved";
  const amt = (v) => `$${parseFloat(v).toFixed(2)}`;
  if (refund.customerChoice === "ORIGINAL") return `Resolved — ${amt(refund.approvedAmount)} refunded`;
  if (refund.customerChoice === "CREDIT") {
    return `Resolved — ${amt(refund.creditTotal)} ${refund.creditScope === "RESTAURANT" ? "store credit" : "to wallet"}`;
  }
  return `Resolved — ${amt(refund.approvedAmount)} to wallet`;
}

function StatusPill({ status, refund }) {
  if (refund?.status === "AWAITING_CUSTOMER") {
    return (
      <Chip
        icon={<CheckCircleIcon sx={{ fontSize: 14, color: "#FF3008 !important" }} />}
        label="Approved — choose your refund"
        size="small"
        sx={{ bgcolor: "#fff5f2", color: "#FF3008", fontWeight: 700, fontSize: "0.72rem", height: 24, border: "1px solid #FF3008" }}
      />
    );
  }
  if (status === "RESOLVED") {
    return (
      <Chip
        icon={<CheckCircleIcon sx={{ fontSize: 14, color: "#15803d !important" }} />}
        label={resolvedLabel(refund)}
        size="small"
        sx={{ bgcolor: "#dcfce7", color: "#15803d", fontWeight: 700, fontSize: "0.72rem", height: 24, border: "1px solid #86efac" }}
      />
    );
  }
  if (status === "CLOSED_NO_ACTION") {
    return (
      <Chip
        icon={<CancelIcon sx={{ fontSize: 14, color: "#64748b !important" }} />}
        label="Closed — no action"
        size="small"
        sx={{ bgcolor: "#f1f5f9", color: "#64748b", fontWeight: 700, fontSize: "0.72rem", height: 24, border: "1px solid #cbd5e1" }}
      />
    );
  }
  return (
    <Chip
      icon={<HourglassEmptyIcon sx={{ fontSize: 14, color: "#b45309 !important" }} />}
      label="Under review"
      size="small"
      sx={{ bgcolor: "#fef3c7", color: "#b45309", fontWeight: 700, fontSize: "0.72rem", height: 24, border: "1px solid #fcd34d" }}
    />
  );
}

// Coloured stripe down the left edge per status — at-a-glance triage.
const ACCENT = {
  RESOLVED: "#16a34a",
  CLOSED: "#64748b",
  OPEN: "#f59e0b",
};

function TicketCard({ ticket, onClick }) {
  const refund = ticket.refundRequest;
  const bucket = bucketOf(ticket.status);
  const accent = refund?.status === "AWAITING_CUSTOMER" ? "#FF3008" : ACCENT[bucket];
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        position: "relative",
        p: 0,
        borderRadius: "16px",
        cursor: "pointer",
        bgcolor: "#fff",
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        transition: "all 0.18s ease",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 10px 28px rgba(15,23,42,0.10)",
          borderColor: accent,
        },
      }}
    >
      <Box sx={{ display: "flex" }}>
        {/* Left accent bar */}
        <Box sx={{ width: 6, bgcolor: accent, flexShrink: 0 }} />

        {/* Body */}
        <Box sx={{ flex: 1, p: 2.25 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.5} sx={{ mb: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#FF3008", letterSpacing: 0.2 }}>
                {ticket.ticketNumber}
              </Typography>
              <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 0.3 }}>
                <ReceiptLongIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                <Typography sx={{ fontSize: "0.8rem", color: "#475569", fontWeight: 600 }}>
                  Order #{ticket.orderDisplayId || ticket.orderId}
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>·</Typography>
                <Typography sx={{ fontSize: "0.8rem", color: "#475569", fontWeight: 600 }}>
                  {ISSUE_LABELS[ticket.issueType] || ticket.issueType}
                </Typography>
              </Stack>
            </Box>
            <StatusPill status={ticket.status} refund={refund} />
          </Stack>

          {refund && (
            <Box sx={{
              mt: 1, mb: 1, p: 1.25, borderRadius: "10px",
              bgcolor: "#f8fafc", border: "1px solid #e2e8f0",
              display: "flex", flexWrap: "wrap", gap: 2,
            }}>
              <Box>
                <Typography sx={{ fontSize: "0.66rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Requested
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#111827" }}>
                  ${parseFloat(refund.requestedAmount).toFixed(2)}
                </Typography>
              </Box>
              {refund.approvedAmount != null && (
                <Box>
                  <Typography sx={{ fontSize: "0.66rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Approved
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: accent }}>
                    ${parseFloat(refund.approvedAmount).toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
            <EventIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
            <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8", fontWeight: 600 }}>
              {new Date(ticket.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
}

export default function MyTickets() {
  const navigate = useNavigate();
  const { isLoggedIn } = useContext(UserContext);
  const { data, loading } = useQuery(MY_TICKETS, { skip: !isLoggedIn, fetchPolicy: "cache-and-network" });
  const [filter, setFilter] = useState("ALL");

  const tickets = useMemo(
    () => [...(data?.myTickets ?? [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [data]
  );

  const counts = useMemo(() => ({
    ALL: tickets.length,
    OPEN: tickets.filter(t => bucketOf(t.status) === "OPEN").length,
    RESOLVED: tickets.filter(t => bucketOf(t.status) === "RESOLVED").length,
    CLOSED: tickets.filter(t => bucketOf(t.status) === "CLOSED").length,
  }), [tickets]);

  const filtered = useMemo(
    () => (filter === "ALL" ? tickets : tickets.filter(t => bucketOf(t.status) === filter)),
    [tickets, filter]
  );

  const dateStr = new Date().toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric",
  });

  return (
    <>
      <Header />

      <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
        {/* Hero banner */}
        <Box sx={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 55%, #FF3008 130%)",
          pt: { xs: 10, md: 12 },
          pb: { xs: 6, md: 8 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""', position: "absolute", top: -120, right: -120,
            width: 380, height: 380, borderRadius: "50%",
            background: "rgba(255,255,255,0.04)",
          },
          "&::after": {
            content: '""', position: "absolute", bottom: -160, left: -100,
            width: 320, height: 320, borderRadius: "50%",
            background: "rgba(255,255,255,0.03)",
          },
        }}>
          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} gap={2.5}>
              <Box sx={{
                width: { xs: 52, md: 60 }, height: { xs: 52, md: 60 }, borderRadius: "14px",
                background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)",
                border: "1px solid rgba(255,255,255,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <SupportAgentIcon sx={{ color: "#93c5fd", fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{
                  fontWeight: 800, fontSize: { xs: "1.6rem", md: "2.1rem" },
                  color: "#fff", lineHeight: 1.15, letterSpacing: "-0.02em",
                }}>
                  My Support Tickets
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.7)", mt: 0.4 }}>
                  {dateStr} &middot; Refunds and order issues
                </Typography>
              </Box>
              {!loading && tickets.length > 0 && (
                <Box sx={{
                  px: 2.5, py: 1.25, borderRadius: "14px",
                  background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  textAlign: "center", minWidth: 110,
                }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.7rem", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
                    Total
                  </Typography>
                  <Typography sx={{ color: "#fff", fontSize: "1.6rem", fontWeight: 800, lineHeight: 1 }}>
                    {tickets.length}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Container>
        </Box>

        <Container maxWidth="md" sx={{ mt: -4, pb: 6, position: "relative", zIndex: 2 }}>
          {/* Filter pills */}
          {!loading && tickets.length > 0 && (
            <Box sx={{
              mb: 3, p: 1, bgcolor: "#fff", borderRadius: "16px",
              border: "1px solid #e5e7eb", display: "inline-flex", gap: 0.5, flexWrap: "wrap",
              boxShadow: "0 6px 18px rgba(15,23,42,0.06)",
            }}>
              {FILTERS.map(f => {
                const Icon = f.icon;
                const active = filter === f.key;
                return (
                  <Box
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    sx={{
                      display: "inline-flex", alignItems: "center", gap: 0.75,
                      px: 2, py: 0.75, borderRadius: "12px", cursor: "pointer",
                      bgcolor: active ? "#FF3008" : "transparent",
                      color: active ? "#fff" : "#475569",
                      transition: "all 0.15s ease",
                      "&:hover": { bgcolor: active ? "#FF3008" : "#f1f5f9" },
                    }}
                  >
                    <Icon sx={{ fontSize: 16 }} />
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>
                      {f.label}
                    </Typography>
                    <Box sx={{
                      bgcolor: active ? "rgba(255,255,255,0.22)" : "#e5e7eb",
                      color: active ? "#fff" : "#475569",
                      fontSize: "0.7rem", fontWeight: 800,
                      px: 0.75, py: 0.05, borderRadius: "8px",
                      minWidth: 22, textAlign: "center",
                    }}>
                      {counts[f.key]}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress sx={{ color: "#FF3008" }} />
            </Box>
          )}

          {!loading && tickets.length === 0 && (
            <Box sx={{
              textAlign: "center", py: 10, bgcolor: "#fff", borderRadius: "16px",
              border: "1px solid #e5e7eb",
            }}>
              <Box sx={{
                width: 80, height: 80, borderRadius: "50%",
                bgcolor: "#eff6ff", display: "inline-flex",
                alignItems: "center", justifyContent: "center", mb: 2,
              }}>
                <SupportAgentIcon sx={{ fontSize: 40, color: "#1d4ed8" }} />
              </Box>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: "#111827", mb: 0.5 }}>
                No tickets yet
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", color: "#6b7280", mb: 2 }}>
                Something wrong with an order? Open a ticket from the order details page.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/orders")}
                sx={{
                  bgcolor: "#FF3008", color: "#fff", fontWeight: 700,
                  textTransform: "none", borderRadius: "10px", px: 3, py: 1,
                  "&:hover": { bgcolor: "#e02b00" },
                }}
              >
                View my orders
              </Button>
            </Box>
          )}

          {!loading && tickets.length > 0 && filtered.length === 0 && (
            <Box sx={{ textAlign: "center", py: 6, bgcolor: "#fff", borderRadius: "16px", border: "1px solid #e5e7eb" }}>
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#475569" }}>
                No tickets in this category
              </Typography>
            </Box>
          )}

          <Stack spacing={2}>
            {filtered.map(t => (
              <TicketCard
                key={t.id}
                ticket={t}
                onClick={() => navigate(`/ticket-detail/${t.id}`)}
              />
            ))}
          </Stack>
        </Container>
      </Box>

    </>
  );
}
